import { FC, useState, useMemo, useEffect, useCallback } from 'react'
import { KTSVG } from '../../../../_metronic/helpers'
import {
  Delivery,
  DeliveryStatus,
  DeliveryPriority,
  ProcurementAuditLogSummary,
  PurchaseOrder,
} from '../core/_models'
import {
  getDeliveriesPage,
  getProcurementLogsByReferences,
  getPurchaseOrdersPage,
  getRequisitions,
  uploadDeliveryProof,
} from '../core/_requests'
import ProcurementLogModal from './ProcurementLogModal'
import ProcurementFilterPanel, { FilterOption } from './ProcurementFilterPanel'
import ProcurementAttachmentGallery from './ProcurementAttachmentGallery'
import ProcurementDocumentPreviewModal from './ProcurementDocumentPreviewModal'
import VesselLandingLink from '../../../shared/components/VesselLandingLink'
import { useProcurementPermissions } from '../core/permissionAccess'
import { PROCUREMENT_PERMS } from '../core/permissions'
import { useGlobalFilter } from '../../../shared/context/GlobalFilterContext'
import {
  getCompanyNameFromEntity,
  getEquipmentNameFromLine,
  getItemDisplayNameFromLine,
  getSubcomponentNameFromLine,
  getVesselNameFromEntity,
  uniqueJoinedValues,
} from '../core/technicalItemContext'
import SearchableSelect from '../../../shared/components/SearchableSelect'

type DeliveryRecord = {
  id: number
  deliveryNumber?: string
  poNumber: string
  rfqNumber?: string | null
  goodsReceiptReference?: string | null
  requisitionNo?: string | null
  requisitionName?: string | null
  status: DeliveryStatus
  priority: DeliveryPriority
  itemName: string
  equipmentName?: string | null
  subcomponentName?: string | null
  vendorName: string
  companyName?: string | null
  portName: string
  vesselId?: number | null
  vesselName?: string | null
  receivedBy?: string | null
  department?: string | null
  catalogue?: string | null
  approvalPendingWith?: string | null
  dueDate: string
  value: number
  fullyReceived: boolean
  invoiced: boolean
  paid: boolean
  proofAttachments?: Array<{fileName: string; url: string; sizeBytes: number}>
}

interface ViewDeliveryModalProps {
  visible: boolean
  onClose: () => void
  record: DeliveryRecord | null
}

const toProcurementListFilters = (globalState: {
  company: {id: number; type: string} | null
  vessel: {id?: number | null} | null
}) => ({
  ...(globalState.company?.id ? {companyId: globalState.company.id, companyType: globalState.company.type} : {}),
  ...(globalState.vessel?.id ? {vesselId: Number(globalState.vessel.id)} : {}),
})

const ViewDeliveryModal: FC<ViewDeliveryModalProps> = ({visible, onClose, record}) => {
  if (!visible || !record) return null

  return (
    <ProcurementDocumentPreviewModal
      visible={visible}
      onClose={onClose}
      title='Delivery Details'
      subtitle={`${record.poNumber} | ${record.requisitionNo || '-'}`}
      toolbar={<span className='badge bg-light-primary text-primary fw-bold'>{record.status}</span>}
    >
      <div className='procurement-doc'>
        <div className='row g-4 mb-4'>
          <div className='col-md-4'>
            <div className='text-muted fs-8 fw-semibold'>PO Number</div>
            <div className='fw-bold'>{record.poNumber}</div>
          </div>
          <div className='col-md-4'>
            <div className='text-muted fs-8 fw-semibold'>Requisition No</div>
            <div className='fw-bold'>{record.requisitionNo || '-'}</div>
          </div>
          <div className='col-md-4'>
            <div className='text-muted fs-8 fw-semibold'>Requisition Title</div>
            <div className='fw-bold'>{record.requisitionName || '-'}</div>
          </div>
          <div className='col-md-4'>
            <div className='text-muted fs-8 fw-semibold'>Equipment</div>
            <div className='fw-bold'>{record.equipmentName || '-'}</div>
          </div>
          <div className='col-md-4'>
            <div className='text-muted fs-8 fw-semibold'>Subcomponent</div>
            <div className='fw-bold'>{record.subcomponentName || '-'}</div>
          </div>
          <div className='col-md-4'>
            <div className='text-muted fs-8 fw-semibold'>Company</div>
            <div className='fw-bold'>{record.companyName || '-'}</div>
          </div>
          <div className='col-md-4'>
            <div className='text-muted fs-8 fw-semibold'>Vessel</div>
            <div className='fw-bold'>
              <VesselLandingLink
                vesselId={record.vesselId ?? null}
                label={record.vesselName || '-'}
                className='text-dark text-decoration-underline'
              />
            </div>
          </div>
          <div className='col-md-4'>
            <div className='text-muted fs-8 fw-semibold'>Vendor</div>
            <div className='fw-bold'>{record.vendorName}</div>
          </div>
          <div className='col-md-4'>
            <div className='text-muted fs-8 fw-semibold'>Delivery Port</div>
            <div className='fw-bold'>{record.portName}</div>
          </div>
          <div className='col-md-4'>
            <div className='text-muted fs-8 fw-semibold'>Due Date</div>
            <div className='fw-bold'>{record.dueDate || '-'}</div>
          </div>
          <div className='col-md-4'>
            <div className='text-muted fs-8 fw-semibold'>Received By</div>
            <div className='fw-bold'>{record.receivedBy || '-'}</div>
          </div>
        </div>

        <div className='mb-3'>
          <h5 className='fw-bold text-primary mb-3'>Proof Attachments</h5>
          <ProcurementAttachmentGallery
            attachments={record.proofAttachments || []}
            emptyText='No proof attachments uploaded yet.'
          />
        </div>
      </div>
    </ProcurementDocumentPreviewModal>
  )
}

const DeliveryTracking: FC = () => {
  const {
    can: canProcurementPermission,
    loading: procurementPermissionsLoading,
  } = useProcurementPermissions()
  const canViewDelivery = canProcurementPermission(PROCUREMENT_PERMS.READ_DELIVERY_TRACKING)
  const canUpdateDelivery = canProcurementPermission(PROCUREMENT_PERMS.UPDATE_DELIVERY_TRACKING)
  const canViewProcurementLog = canProcurementPermission(PROCUREMENT_PERMS.PROCUREMENT_LOG_VIEW)

  const mapDeliveryRecord = (d: Delivery, poMap: Map<number, PurchaseOrder>): DeliveryRecord => {
    const po = poMap.get(Number(d.purchaseOrderId))
    const req = (po as any)?.rfq?.requisition
    const reqLines = (req?.lines || []) as any[]
    const vesselId =
      Number((d as any).vesselId ?? req?.vesselId ?? req?.vessel?.id ?? (po as any)?.vesselId ?? 0) || null
    const itemName =
      d.itemName ||
      uniqueJoinedValues(reqLines.map((line: any, idx: number) => getItemDisplayNameFromLine(line, idx)))
    return {
      id: d.id,
      deliveryNumber: d.deliveryNumber,
      poNumber: d.poNumber,
      rfqNumber: d.rfqNumber || null,
      goodsReceiptReference: d.goodsReceiptReference || null,
      requisitionNo: d.requisitionNo || po?.requisitionNo || req?.requisition_code || null,
      requisitionName: d.requisitionName || req?.name || req?.title || null,
      status: d.status,
      priority: d.priority,
      itemName: itemName === '-' ? d.itemName : itemName,
      equipmentName:
        (d as any).equipmentName ||
        uniqueJoinedValues(reqLines.map((line: any) => getEquipmentNameFromLine(line))),
      subcomponentName:
        (d as any).subcomponentName ||
        uniqueJoinedValues(reqLines.map((line: any) => getSubcomponentNameFromLine(line))),
      vendorName: d.vendorName || po?.vendor?.name || '-',
      companyName: (d as any).companyName || getCompanyNameFromEntity(req || po) || null,
      portName: d.portName,
      vesselId,
      vesselName: (d as any).vesselName || getVesselNameFromEntity(req || po) || null,
      receivedBy: (d as any).receivedBy || null,
      department: (d as any).department || req?.inventoryItemHead?.name || req?.requestFor || null,
      catalogue: (d as any).catalogue || req?.category?.name || null,
      approvalPendingWith: (d as any).approvalPendingWith || null,
      dueDate: d.dueDate,
      value: d.value,
      fullyReceived: d.fullyReceived,
      invoiced: d.invoiced,
      paid: d.paid,
      proofAttachments: d.proofAttachments || [],
    }
  }

  const { state: globalState } = useGlobalFilter()
  const globalCompanyId = globalState.company?.id ?? null
  const globalCompanyType = globalState.company?.type ?? null
  const globalVesselId = globalState.vessel?.id ? Number(globalState.vessel.id) : null
  const [searchTerm, setSearchTerm] = useState('')
  const [currentPage, setCurrentPage] = useState(1)
  const [rowsPerPage, setRowsPerPage] = useState(10)
  const [deliveryData, setDeliveryData] = useState<DeliveryRecord[]>([])
  const [loading, setLoading] = useState(true)
  const [referenceFilter, setReferenceFilter] = useState('')
  const [vesselFilter, setVesselFilter] = useState('')

  // Sync global header vessel filter → local vessel filter (name-based)
  useEffect(() => {
    const gv = globalState.vessel
    setVesselFilter(gv?.fleet_name || gv?.name || '')
  }, [globalState.vessel])
  const [departmentFilter, setDepartmentFilter] = useState('')
  const [catalogueFilter, setCatalogueFilter] = useState('')
  const [requisitionTitleFilter, setRequisitionTitleFilter] = useState('')
  const [approvalPendingWithFilter, setApprovalPendingWithFilter] = useState('')
  const [poDateFromFilter, setPoDateFromFilter] = useState('')
  const [poDateToFilter, setPoDateToFilter] = useState('')
  const [deliveredToVesselFilter, setDeliveredToVesselFilter] = useState('')
  const [stageFilter, setStageFilter] = useState('')
  const [additionalFiltersOpen, setAdditionalFiltersOpen] = useState(false)
  const [procurementLogs, setProcurementLogs] = useState<ProcurementAuditLogSummary[]>([])
  const [logLoading, setLogLoading] = useState(false)
  const [logLoadingMore, setLogLoadingMore] = useState(false)
  const [logHasMore, setLogHasMore] = useState(false)
  const [logPage, setLogPage] = useState(0)
  const [logReferences, setLogReferences] = useState<string[]>([])
  const [logModalVisible, setLogModalVisible] = useState(false)
  const [logModalTitle, setLogModalTitle] = useState('')
  const [isViewModalVisible, setIsViewModalVisible] = useState(false)
  const [selectedDelivery, setSelectedDelivery] = useState<DeliveryRecord | null>(null)
  const [sortConfig, setSortConfig] = useState<{
    key: keyof DeliveryRecord | null;
    direction: 'asc' | 'desc';
  }>({ key: null, direction: 'asc' })

  const loadDeliveries = useCallback(async () => {
    try {
      setLoading(true)
      const pageSize = 200
      const deliveries: Delivery[] = []
      const purchaseOrders: PurchaseOrder[] = []
      const listFilters = toProcurementListFilters(globalState)

      let deliveryPage = 0
      while (true) {
        const pageData = await getDeliveriesPage(deliveryPage, pageSize, listFilters)
        const content = pageData.content || []
        deliveries.push(...content)
        const lastPage =
          pageData.last === true ||
          content.length < pageSize ||
          deliveryPage >= Math.max((pageData.totalPages ?? 1) - 1, 0)
        deliveryPage += 1
        if (lastPage || deliveryPage > 200) break
      }

      let poPage = 0
      while (true) {
        const pageData = await getPurchaseOrdersPage(poPage, pageSize, listFilters)
        const content = pageData.content || []
        purchaseOrders.push(...content)
        const lastPage =
          pageData.last === true ||
          content.length < pageSize ||
          poPage >= Math.max((pageData.totalPages ?? 1) - 1, 0)
        poPage += 1
        if (lastPage || poPage > 200) break
      }

      const poMap = new Map<number, PurchaseOrder>(purchaseOrders.map((po) => [Number(po.id), po]))
      const mappedData: DeliveryRecord[] = deliveries.map((delivery) => mapDeliveryRecord(delivery, poMap))
      const mappedReqCodes = new Set(
        mappedData.map((record) => String(record.requisitionNo || '').trim()).filter(Boolean)
      )

      const reqPage = await getRequisitions(0, 500).catch(() => null)
      const fallbackReqRecords: DeliveryRecord[] = (reqPage?.content || [])
        .filter((req: any) => {
          const reqCode = String(req?.requisition_code || '').trim()
          return Boolean(reqCode) && !mappedReqCodes.has(reqCode)
        })
        .map((req: any) => {
          const reqLines = Array.isArray(req?.lines) ? req.lines : []
          const totalValue =
            Number(req?.estimatedTotalValue) ||
            (reqLines.length
              ? reqLines.reduce(
                  (acc: number, line: any) =>
                    acc + Number(line?.quantity || 0) * Number(line?.estimatedUnitPrice || 0),
                  0
                )
              : Number(req?.quantity || 0) * Number(req?.estimatedUnitPrice || 0))
          const fallbackRecord: DeliveryRecord = {
            id: Number(`9${req.id}`),
            deliveryNumber: undefined,
            poNumber: 'N/A',
            requisitionNo: req?.requisition_code || null,
            requisitionName: req?.name || req?.title || null,
            status: 'PREPARING',
            priority: 'MEDIUM',
            itemName: req?.primaryItemName || req?.title || req?.description || 'Requisition',
            equipmentName: uniqueJoinedValues(reqLines.map((line: any) => getEquipmentNameFromLine(line))),
            subcomponentName: uniqueJoinedValues(
              reqLines.map((line: any) => getSubcomponentNameFromLine(line))
            ),
            vendorName: '-',
            companyName: getCompanyNameFromEntity(req) || null,
            portName: req?.customPortName || req?.requestedPortName || '-',
            vesselName: getVesselNameFromEntity(req) || null,
            receivedBy: null,
            department: req?.inventoryItemHead?.name || req?.requestFor || null,
            catalogue: req?.category?.name || null,
            approvalPendingWith: 'Company',
            dueDate: req?.requiredDeliveryDate || req?.createdAt || '',
            value: Number.isFinite(totalValue) ? totalValue : 0,
            fullyReceived: false,
            invoiced: false,
            paid: false,
            proofAttachments: [],
          }
          return fallbackRecord
        })

      setDeliveryData([...mappedData, ...fallbackReqRecords])
    } catch (error) {
      console.error('Error fetching deliveries:', error)
    } finally {
      setLoading(false)
    }
  }, [globalCompanyId, globalCompanyType, globalVesselId])

  useEffect(() => {
    if (procurementPermissionsLoading || !canViewDelivery) return
    loadDeliveries()
  }, [loadDeliveries, procurementPermissionsLoading, canViewDelivery])

  useEffect(() => {
    setCurrentPage(1)
  }, [globalCompanyId, globalCompanyType, globalVesselId])

  const stageOptions: FilterOption[] = [
    { value: '', label: 'All Stages' },
    { value: 'PREPARING', label: 'Preparing' },
    { value: 'IN_TRANSIT', label: 'In Transit' },
    { value: 'DELIVERED', label: 'Delivered' },
    { value: 'DELAYED', label: 'Delayed' },
  ]

  const approvalPendingWithOptions: FilterOption[] = [
    { value: '', label: 'All' },
    { value: 'SHIP', label: 'Ship' },
    { value: 'PORT', label: 'Port' },
    { value: 'VENDOR', label: 'Vendor' },
    { value: 'NONE', label: 'None' },
  ]



  const departmentOptions = useMemo<FilterOption[]>(() => {
    const values = Array.from(new Set(deliveryData.map((d) => d.department).filter(Boolean))).sort()
    return [{ value: '', label: 'All Departments' }, ...values.map((v) => ({ value: String(v), label: String(v) }))]
  }, [deliveryData])

  const catalogueOptions = useMemo<FilterOption[]>(() => {
    const values = Array.from(new Set(deliveryData.map((d) => d.catalogue).filter(Boolean))).sort()
    return [{ value: '', label: 'All Catalogues' }, ...values.map((v) => ({ value: String(v), label: String(v) }))]
  }, [deliveryData])

  const filteredData = useMemo(() => {
    return deliveryData.filter(record => {
      const matchesSearch = searchTerm === '' || 
        (record.requisitionNo || '').toLowerCase().includes(searchTerm.toLowerCase()) ||
        (record.requisitionName || '').toLowerCase().includes(searchTerm.toLowerCase()) ||
        record.itemName.toLowerCase().includes(searchTerm.toLowerCase()) ||
        (record.equipmentName || '').toLowerCase().includes(searchTerm.toLowerCase()) ||
        (record.subcomponentName || '').toLowerCase().includes(searchTerm.toLowerCase()) ||
        (record.companyName || '').toLowerCase().includes(searchTerm.toLowerCase()) ||
        (record.vesselName || '').toLowerCase().includes(searchTerm.toLowerCase()) ||
        record.vendorName.toLowerCase().includes(searchTerm.toLowerCase()) ||
        record.portName.toLowerCase().includes(searchTerm.toLowerCase()) ||
        record.poNumber.toLowerCase().includes(searchTerm.toLowerCase())

      const matchesReference =
        referenceFilter === '' ||
        record.poNumber.toLowerCase().includes(referenceFilter.toLowerCase()) ||
        (record.requisitionNo || '').toLowerCase().includes(referenceFilter.toLowerCase())

      const matchesVessel =
        vesselFilter === '' ||
        (record.vesselName || '').toLowerCase().includes(vesselFilter.toLowerCase())

      const matchesDepartment =
        departmentFilter === '' ||
        (record.department || '').toLowerCase().includes(departmentFilter.toLowerCase())

      const matchesCatalogue =
        catalogueFilter === '' ||
        (record.catalogue || '').toLowerCase().includes(catalogueFilter.toLowerCase())

      const matchesReqTitle =
        requisitionTitleFilter === '' ||
        (record.requisitionName || '').toLowerCase().includes(requisitionTitleFilter.toLowerCase())

      const pendingWith = record.approvalPendingWith || (record.status === 'DELIVERED' ? 'NONE' : 'SHIP')
      const matchesApprovalPending =
        approvalPendingWithFilter === '' || pendingWith.toUpperCase() === approvalPendingWithFilter

      const matchesStage = stageFilter === '' || record.status === stageFilter
      const matchesDateFrom = poDateFromFilter === '' || record.dueDate >= poDateFromFilter
      const matchesDateTo = poDateToFilter === '' || record.dueDate <= poDateToFilter
      const deliveredFlag = record.status === 'DELIVERED' ? 'YES' : 'NO'
      const matchesDelivered =
        deliveredToVesselFilter === '' || deliveredFlag === deliveredToVesselFilter

      return (
        matchesSearch &&
        matchesReference &&
        matchesVessel &&
        matchesDepartment &&
        matchesCatalogue &&
        matchesReqTitle &&
        matchesApprovalPending &&
        matchesStage &&
        matchesDateFrom &&
        matchesDateTo &&
        matchesDelivered
      )
    })
  }, [
    searchTerm,
    deliveryData,
    referenceFilter,
    vesselFilter,
    departmentFilter,
    catalogueFilter,
    requisitionTitleFilter,
    approvalPendingWithFilter,
    stageFilter,
    poDateFromFilter,
    poDateToFilter,
    deliveredToVesselFilter,
  ])

  const sortedData = useMemo(() => {
    let sortedRecords = [...filteredData]

    if (sortConfig.key !== null) {
      sortedRecords.sort((a, b) => {
        const aVal = String(a[sortConfig.key!] || '').toLowerCase()
        const bVal = String(b[sortConfig.key!] || '').toLowerCase()

        if (aVal < bVal) return sortConfig.direction === 'asc' ? -1 : 1
        if (aVal > bVal) return sortConfig.direction === 'asc' ? 1 : -1
        return 0
      })
    }
    return sortedRecords
  }, [filteredData, sortConfig])

  const handleSort = (key: keyof DeliveryRecord) => {
    setSortConfig(prev => ({
      key,
      direction: prev.key === key && prev.direction === 'asc' ? 'desc' : 'asc',
    }))
  }

  const filteredTotalElements = sortedData.length
  const currentRecords = sortedData.slice((currentPage - 1) * rowsPerPage, currentPage * rowsPerPage)
  const totalPages = Math.max(1, Math.ceil(filteredTotalElements / rowsPerPage))

  useEffect(() => {
    if (currentPage > totalPages) {
      setCurrentPage(totalPages)
    }
  }, [currentPage, totalPages])

  const handlePageChange = (page: number) => {
    if (page > 0 && page <= totalPages) {
      setCurrentPage(page)
    }
  }

  const handleRowsPerPageChange = (e: React.ChangeEvent<HTMLSelectElement>) => {
    setRowsPerPage(parseInt(e.target.value))
    setCurrentPage(1)
  }

  const getStatusBadge = (status: DeliveryStatus) => {
    const statusColors: Record<DeliveryStatus, string> = {
      'IN_TRANSIT': 'bg-primary',
      'PREPARING': 'bg-warning',
      'DELIVERED': 'bg-success',
      'DELAYED': 'bg-danger'
    }
    const statusLabels: Record<DeliveryStatus, string> = {
      'IN_TRANSIT': 'In Transit',
      'PREPARING': 'Preparing',
      'DELIVERED': 'Delivered',
      'DELAYED': 'Delayed'
    }
    return (
      <span className={`badge ${statusColors[status]} text-white`}>
        {statusLabels[status]}
      </span>
    )
  }

  const getPriorityBadge = (priority: DeliveryPriority) => {
    const priorityColors: Record<DeliveryPriority, string> = {
      'HIGH': 'bg-danger',
      'MEDIUM': 'bg-warning',
      'LOW': 'bg-info'
    }
    const priorityLabels: Record<DeliveryPriority, string> = {
      'HIGH': 'High Priority',
      'MEDIUM': 'Medium Priority',
      'LOW': 'Low Priority'
    }
    return (
      <span className={`badge ${priorityColors[priority]} text-white`}>
        {priorityLabels[priority]}
      </span>
    )
  }

  const handleViewLog = async (record: DeliveryRecord) => {
    if (!canViewProcurementLog) {
      window.alert('You do not have audit trail access.')
      return
    }
    const references = Array.from(
      new Set(
        [
          record.poNumber,
          record.rfqNumber,
          record.requisitionNo,
          record.goodsReceiptReference,
          record.deliveryNumber,
        ]
          .map((value) => String(value || '').trim())
          .filter(Boolean)
      )
    )

    if (references.length === 0) {
      window.alert('No reference found to load procurement logs')
      return
    }

    try {
      setLogModalVisible(true)
      setLogLoading(true)
      setLogLoadingMore(false)
      setLogModalTitle(`Complete Tracking History (${references.join(', ')})`)
      setLogReferences(references)
      const pageData = await getProcurementLogsByReferences(references, {page: 0, size: 25})
      setProcurementLogs(pageData.content || [])
      setLogPage(pageData.number ?? 0)
      setLogHasMore(!(pageData.last ?? true))
    } catch (error: any) {
      window.alert('Failed to load procurement logs')
      setProcurementLogs([])
      setLogHasMore(false)
    } finally {
      setLogLoading(false)
    }
  }

  const handleLoadMoreLogs = useCallback(async () => {
    if (logLoading || logLoadingMore || !logHasMore || logReferences.length === 0) {
      return
    }
    try {
      setLogLoadingMore(true)
      const nextPage = logPage + 1
      const pageData = await getProcurementLogsByReferences(logReferences, {
        page: nextPage,
        size: 25,
      })
      const nextLogs = pageData.content || []
      setProcurementLogs((prev) => {
        const merged = [...prev]
        const seenIds = new Set(prev.map((log) => log.id))
        nextLogs.forEach((log) => {
          if (!seenIds.has(log.id)) {
            seenIds.add(log.id)
            merged.push(log)
          }
        })
        return merged
      })
      setLogPage(pageData.number ?? nextPage)
      setLogHasMore(!(pageData.last ?? true))
    } catch (error) {
      window.alert('Failed to load more procurement logs')
    } finally {
      setLogLoadingMore(false)
    }
  }, [logHasMore, logLoading, logLoadingMore, logPage, logReferences])

  const handleUploadProof = async (deliveryId: number, fileList: FileList | null) => {
    if (!canUpdateDelivery) {
      window.alert('You do not have permission to upload delivery proof.')
      return
    }
    if (!fileList || fileList.length === 0) return
    try {
      await uploadDeliveryProof(deliveryId, Array.from(fileList))
      await loadDeliveries()
    } catch (error: any) {
      window.alert('Failed to upload proof attachment')
    }
  }

  const handleViewDelivery = (record: DeliveryRecord) => {
    setSelectedDelivery(record)
    setIsViewModalVisible(true)
  }

  const resetFilters = () => {
    setSearchTerm('')
    setReferenceFilter('')
    setVesselFilter('')
    setDepartmentFilter('')
    setCatalogueFilter('')
    setRequisitionTitleFilter('')
    setApprovalPendingWithFilter('')
    setPoDateFromFilter('')
    setPoDateToFilter('')
    setDeliveredToVesselFilter('')
    setStageFilter('')
    setCurrentPage(1)
  }

  if (procurementPermissionsLoading) {
    return (
      <div className='d-flex align-items-center justify-content-center py-10'>
        <div className='spinner-border text-primary' role='status'>
          <span className='visually-hidden'>Loading procurement permissions...</span>
        </div>
      </div>
    )
  }

  if (!canViewDelivery) {
    return <div className='alert alert-warning m-6'>You do not have delivery tracking view access.</div>
  }

  return (
    <div className='app-main flex-column flex-row-fluid' id='kt_app_main'>
      <div className='d-flex flex-column flex-column-fluid'>
        <div id='kt_app_content' className='app-content flex-column-fluid bg-white'>
          <div className='card'>
            <div className='card-header border-0 pt-6 bg-white'>
              <div>
                <h3 className='card-label text-dark fw-bold'>Delivery Tracking</h3>
                <p className='text-muted mb-0'>Track requisitions and delivery progress</p>
              </div>
            </div>

            <div className='card-body py-4 bg-white border-top'>
              <ProcurementFilterPanel
                searchValue={searchTerm}
                onSearchChange={(value) => {
                  setSearchTerm(value)
                  setCurrentPage(1)
                }}
                searchPlaceholder='Search deliveries...'
                referenceValue={referenceFilter}
                onReferenceChange={(value) => {
                  setReferenceFilter(value)
                  setCurrentPage(1)
                }}
                departmentValue={departmentFilter}
                onDepartmentChange={(value) => {
                  setDepartmentFilter(value)
                  setCurrentPage(1)
                }}
                departmentOptions={departmentOptions}
                catalogueValue={catalogueFilter}
                onCatalogueChange={(value) => {
                  setCatalogueFilter(value)
                  setCurrentPage(1)
                }}
                catalogueOptions={catalogueOptions}
                requisitionTitleValue={requisitionTitleFilter}
                onRequisitionTitleChange={(value) => {
                  setRequisitionTitleFilter(value)
                  setCurrentPage(1)
                }}
                approvalPendingWithValue={approvalPendingWithFilter}
                onApprovalPendingWithChange={(value) => {
                  setApprovalPendingWithFilter(value)
                  setCurrentPage(1)
                }}
                approvalPendingWithOptions={approvalPendingWithOptions}
                poDateFrom={poDateFromFilter}
                onPoDateFromChange={(value) => {
                  setPoDateFromFilter(value)
                  setCurrentPage(1)
                }}
                poDateTo={poDateToFilter}
                onPoDateToChange={(value) => {
                  setPoDateToFilter(value)
                  setCurrentPage(1)
                }}
                deliveredToVesselValue={deliveredToVesselFilter}
                onDeliveredToVesselChange={(value) => {
                  setDeliveredToVesselFilter(value)
                  setCurrentPage(1)
                }}
                stageValue={stageFilter}
                onStageChange={(value) => {
                  setStageFilter(value)
                  setCurrentPage(1)
                }}
                stageOptions={stageOptions}
                additionalOpen={additionalFiltersOpen}
                onToggleAdditional={() => setAdditionalFiltersOpen((prev) => !prev)}
                onReset={resetFilters}
              />

              {/* Deliveries Table */}
              <div className='mb-8'>
                <h4 className='fw-bold text-primary mb-4'>Delivery Tracking ({filteredTotalElements})</h4>
                <div className='report-table table-responsive'>
                  <div style={{ overflowX: 'auto' }}>
                    <table className='table table-bordered align-middle procurement-list-table'>
                      <thead className='table-header text-start'>
                        <tr>
                          <th onClick={() => handleSort('poNumber')} className='cursor-pointer align-middle' style={{ padding: '12px 16px' }}>
                            <div className='d-flex align-items-center'>
                              <span style={{ color: '#3F4254', fontWeight: 600 }}>PO Number</span>
                              <div style={{ transform: 'translateY(-2px)' }}>
                                <KTSVG 
                                  path={`/media/map/sort-col-${sortConfig.key === 'poNumber' ? sortConfig.direction === 'asc' ? 'up-black' : 'down-black' : 'grey'}.svg`} 
                                  className='svg-icon ms-2 custom-sort-icon'
                                />
                              </div>
                            </div>
                          </th>
                          <th className='align-middle' style={{ padding: '12px 16px', color: '#3F4254', fontWeight: 600 }}>REQUISITION NO</th>
                          <th className='align-middle' style={{ padding: '12px 16px', color: '#3F4254', fontWeight: 600 }}>REQUISITION TITLE</th>
                          <th className='align-middle' style={{ padding: '12px 16px', color: '#3F4254', fontWeight: 600 }}>Status</th>
                          <th className='align-middle' style={{ padding: '12px 16px', color: '#3F4254', fontWeight: 600 }}>Priority</th>
                          <th onClick={() => handleSort('itemName')} className='cursor-pointer align-middle' style={{ padding: '12px 16px' }}>
                            <div className='d-flex align-items-center'>
                              <span style={{ color: '#3F4254', fontWeight: 600 }}>Item Name</span>
                              <div style={{ transform: 'translateY(-2px)' }}>
                                <KTSVG 
                                  path={`/media/map/sort-col-${sortConfig.key === 'itemName' ? sortConfig.direction === 'asc' ? 'up-black' : 'down-black' : 'grey'}.svg`} 
                                  className='svg-icon ms-2 custom-sort-icon'
                                />
                              </div>
                            </div>
                          </th>
                          <th onClick={() => handleSort('vendorName')} className='cursor-pointer align-middle' style={{ padding: '12px 16px' }}>
                            <div className='d-flex align-items-center'>
                              <span style={{ color: '#3F4254', fontWeight: 600 }}>Vendor</span>
                              <div style={{ transform: 'translateY(-2px)' }}>
                                <KTSVG 
                                  path={`/media/map/sort-col-${sortConfig.key === 'vendorName' ? sortConfig.direction === 'asc' ? 'up-black' : 'down-black' : 'grey'}.svg`} 
                                  className='svg-icon ms-2 custom-sort-icon'
                                />
                              </div>
                            </div>
                          </th>
                          <th className='align-middle' style={{ padding: '12px 16px' }}>
                            <span style={{ color: '#3F4254', fontWeight: 600 }}>Equipment</span>
                          </th>
                          <th className='align-middle' style={{ padding: '12px 16px' }}>
                            <span style={{ color: '#3F4254', fontWeight: 600 }}>Subcomponent</span>
                          </th>
                          <th className='align-middle' style={{ padding: '12px 16px' }}>
                            <span style={{ color: '#3F4254', fontWeight: 600 }}>Company</span>
                          </th>
                          <th className='align-middle' style={{ padding: '12px 16px' }}>
                            <span style={{ color: '#3F4254', fontWeight: 600 }}>Vessel</span>
                          </th>
                          <th onClick={() => handleSort('portName')} className='cursor-pointer align-middle' style={{ padding: '12px 16px' }}>
                            <div className='d-flex align-items-center'>
                              <span style={{ color: '#3F4254', fontWeight: 600 }}>Port</span>
                              <div style={{ transform: 'translateY(-2px)' }}>
                                <KTSVG 
                                  path={`/media/map/sort-col-${sortConfig.key === 'portName' ? sortConfig.direction === 'asc' ? 'up-black' : 'down-black' : 'grey'}.svg`} 
                                  className='svg-icon ms-2 custom-sort-icon'
                                />
                              </div>
                            </div>
                          </th>
                          <th onClick={() => handleSort('dueDate')} className='cursor-pointer align-middle' style={{ padding: '12px 16px' }}>
                            <div className='d-flex align-items-center'>
                              <span style={{ color: '#3F4254', fontWeight: 600 }}>Due Date</span>
                              <div style={{ transform: 'translateY(-2px)' }}>
                                <KTSVG 
                                  path={`/media/map/sort-col-${sortConfig.key === 'dueDate' ? sortConfig.direction === 'asc' ? 'up-black' : 'down-black' : 'grey'}.svg`} 
                                  className='svg-icon ms-2 custom-sort-icon'
                                />
                              </div>
                            </div>
                          </th>
                          <th className='align-middle' style={{ padding: '12px 16px' }}>
                            <span style={{ color: '#3F4254', fontWeight: 600 }}>Received By</span>
                          </th>
                          <th onClick={() => handleSort('value')} className='cursor-pointer align-middle' style={{ padding: '12px 16px' }}>
                            <div className='d-flex align-items-center'>
                              <span style={{ color: '#3F4254', fontWeight: 600 }}>Value</span>
                              <div style={{ transform: 'translateY(-2px)' }}>
                                <KTSVG 
                                  path={`/media/map/sort-col-${sortConfig.key === 'value' ? sortConfig.direction === 'asc' ? 'up-black' : 'down-black' : 'grey'}.svg`} 
                                  className='svg-icon ms-2 custom-sort-icon'
                                />
                              </div>
                            </div>
                          </th>
                          <th className='align-middle text-center' style={{ padding: '12px 16px', color: '#3F4254', fontWeight: 600 }}>Actions</th>
                        </tr>
                      </thead>
                      <tbody className='table-body text-start'>
                        {loading ? (
                          <tr>
                            <td colSpan={16} className='text-center text-muted py-5'>
                              Loading deliveries...
                            </td>
                          </tr>
                        ) : currentRecords.length === 0 ? (
                          <tr>
                            <td colSpan={16} className='text-center text-muted py-5'>
                              No deliveries found for the selected criteria.
                            </td>
                          </tr>
                        ) : (
                          currentRecords.map((record) => (
                            <tr key={record.id} style={{ borderBottom: '1px solid #E4E6EF' }}>
                              <td className='text-dark fs-6' style={{ padding: '12px 16px' }}>{record.poNumber}</td>
                              <td className='text-dark fs-6' style={{ padding: '12px 16px' }}>{record.requisitionNo || '-'}</td>
                              <td className='text-dark fs-6' style={{ padding: '12px 16px' }}>{record.requisitionName || '-'}</td>
                              <td style={{ padding: '12px 16px' }}>{getStatusBadge(record.status)}</td>
                              <td style={{ padding: '12px 16px' }}>{getPriorityBadge(record.priority)}</td>
                              <td className='text-dark fs-6' style={{ padding: '12px 16px', maxWidth: '300px' }}>{record.itemName}</td>
                              <td className='text-dark fs-6' style={{ padding: '12px 16px' }}>{record.vendorName}</td>
                              <td className='text-dark fs-6' style={{ padding: '12px 16px' }}>{record.equipmentName || '-'}</td>
                              <td className='text-dark fs-6' style={{ padding: '12px 16px' }}>{record.subcomponentName || '-'}</td>
                              <td className='text-dark fs-6' style={{ padding: '12px 16px' }}>{record.companyName || '-'}</td>
                              <td className='text-dark fs-6' style={{ padding: '12px 16px' }}>
                                <VesselLandingLink
                                  vesselId={record.vesselId ?? null}
                                  label={record.vesselName || '-'}
                                  className='text-dark text-decoration-underline'
                                />
                              </td>
                              <td className='text-dark fs-6' style={{ padding: '12px 16px' }}>{record.portName}</td>
                              <td className='text-dark fs-6' style={{ padding: '12px 16px' }}>{record.dueDate}</td>
                              <td className='text-dark fs-6' style={{ padding: '12px 16px' }}>{record.receivedBy || '-'}</td>
                              <td className='text-dark fs-6 fw-semibold' style={{ padding: '12px 16px' }}>${record.value.toLocaleString()}</td>
                              <td className='text-center' style={{ padding: '12px 16px' }}>
                                <button
                                  className='btn btn-sm px-0'
                                  title='View'
                                  onClick={() => handleViewDelivery(record)}
                                >
                                  <KTSVG path='/media/map/ph_eye.svg' />
                                </button>
                                {canViewProcurementLog && (
                                  <button
                                    className='btn btn-sm px-0'
                                    title='View Complete Tracking History'
                                    onClick={() => handleViewLog(record)}
                                  >
                                    <KTSVG path='/media/icons/duotune/general/gen055.svg' />
                                  </button>
                                )}
                                {canUpdateDelivery && (
                                  <label className='btn btn-sm px-0 mb-0' title='Upload Proof'>
                                    <input
                                      type='file'
                                      multiple
                                      style={{display: 'none'}}
                                      accept='image/*,.pdf,.doc,.docx,.xls,.xlsx,.txt'
                                      onChange={(e) => handleUploadProof(record.id, e.target.files)}
                                    />
                                    <KTSVG path='/media/icons/duotune/files/fil005.svg' />
                                  </label>
                                )}
                              </td>
                            </tr>
                          ))
                        )}
                      </tbody>
                    </table>
                  </div>

                  {/* Pagination */}
                  <div 
                    className='pagination-wrapper d-flex justify-content-between align-items-center py-3'
                    style={{
                      position: 'static',
                      bottom: 0,
                      backgroundColor: '#fff',
                      zIndex: 10,
                      borderTop: '1px solid #dee2e6',
                      marginTop: 'auto'
                    }}
                  >
                    <div className='d-flex align-items-center'>
                      <span className='text-muted me-2'>Rows per page</span>
                      <SearchableSelect
                        className='form-select'
                        style={{
                          borderRadius: '20px',
                          width: '70px',
                          border: '1px solid #dee2e6',
                          fontSize: '14px',
                          padding: '4px 8px'
                        }}
                        value={rowsPerPage}
                        onChange={handleRowsPerPageChange}
                      >
                        <option value={10}>10</option>
                        <option value={20}>20</option>
                        <option value={50}>50</option>
                      </SearchableSelect>
                    </div>
                    <div className='d-flex align-items-center'>
                      <span className='text-muted me-3' style={{ fontSize: '14px' }}>
                        Showing <strong>{currentRecords.length > 0 ? (currentPage - 1) * rowsPerPage + 1 : 0}-{Math.min(currentPage * rowsPerPage, filteredTotalElements)}</strong> of <strong>{filteredTotalElements}</strong>
                      </span>

                      <nav>
                        <ul className='pagination pagination-sm mb-0' style={{ gap: '2px' }}>
                          <li className={`page-item ${currentPage === 1 ? 'disabled' : ''}`}>
                            <button
                              className='page-link text-muted'
                              style={{
                                backgroundColor: '#f8f9fa',
                                border: '1px solid #dee2e6',
                                padding: '8px 12px',
                                fontSize: '14px',
                                borderRadius: '6px'
                              }}
                              onClick={() => handlePageChange(currentPage - 1)}
                              disabled={currentPage === 1}
                            >
                              ‹
                            </button>
                          </li>

                          {(() => {
                            const pages = []
                            const showPages = 5
                            let startPage = Math.max(1, currentPage - 2)
                            let endPage = Math.min(totalPages, startPage + showPages - 1)

                            if (endPage - startPage < showPages - 1) {
                              startPage = Math.max(1, endPage - showPages + 1)
                            }

                            if (startPage > 1) {
                              pages.push(
                                <li key={1} className='page-item'>
                                  <button
                                    className='page-link text-muted'
                                    style={{
                                      backgroundColor: '#f8f9fa',
                                      border: '1px solid #dee2e6',
                                      padding: '8px 12px',
                                      fontSize: '14px',
                                      minWidth: '40px',
                                      borderRadius: '6px'
                                    }}
                                    onClick={() => handlePageChange(1)}
                                  >
                                    1
                                  </button>
                                </li>
                              )

                              if (startPage > 2) {
                                pages.push(
                                  <li key='ellipsis1' className='page-item disabled'>
                                    <span className='page-link border-0 text-muted' style={{ backgroundColor: 'transparent', padding: '4px 8px' }}>...</span>
                                  </li>
                                )
                              }
                            }

                            for (let i = startPage; i <= endPage; i++) {
                              pages.push(
                                <li key={i} className={`page-item ${currentPage === i ? 'active' : ''}`}>
                                  <button
                                    className='page-link text-muted'
                                    style={{
                                      backgroundColor: currentPage === i ? '#F4F9FF' : 'transparent',
                                      border: '1px solid #dee2e6',
                                      padding: '8px 12px',
                                      fontSize: '14px',
                                      minWidth: '40px',
                                      borderRadius: '6px',
                                      outline: 'none',
                                      boxShadow: 'none'
                                    }}
                                    onClick={() => handlePageChange(i)}
                                  >
                                    {i}
                                  </button>
                                </li>
                              )
                            }

                            if (endPage < totalPages) {
                              if (endPage < totalPages - 1) {
                                pages.push(
                                  <li key='ellipsis2' className='page-item disabled'>
                                    <span className='page-link border-0 text-muted' style={{ backgroundColor: 'transparent', padding: '4px 8px' }}>...</span>
                                  </li>
                                )
                              }

                              pages.push(
                                <li key={totalPages} className='page-item'>
                                  <button
                                    className='page-link text-muted'
                                    style={{
                                      backgroundColor: '#f8f9fa',
                                      border: '1px solid #dee2e6',
                                      padding: '8px 12px',
                                      fontSize: '14px',
                                      minWidth: '40px',
                                      borderRadius: '6px'
                                    }}
                                    onClick={() => handlePageChange(totalPages)}
                                  >
                                    {totalPages}
                                  </button>
                                </li>
                              )
                            }

                            return pages
                          })()}

                          <li className={`page-item ${currentPage === totalPages || totalPages === 0 ? 'disabled' : ''}`}>
                            <button
                              className='page-link text-muted'
                              style={{
                                backgroundColor: '#f8f9fa',
                                border: '1px solid #dee2e6',
                                padding: '8px 12px',
                                fontSize: '14px',
                                borderRadius: '6px'
                              }}
                              onClick={() => handlePageChange(currentPage + 1)}
                              disabled={currentPage === totalPages || totalPages === 0}
                            >
                              ›
                            </button>
                          </li>
                        </ul>
                      </nav>
                    </div>
                  </div>
                </div>
              </div>
            </div>
          </div>
        </div>
      </div>
      <ViewDeliveryModal
        visible={isViewModalVisible}
        onClose={() => {
          setIsViewModalVisible(false)
          setSelectedDelivery(null)
        }}
        record={selectedDelivery}
      />
      <ProcurementLogModal
        visible={logModalVisible}
        onClose={() => {
          setLogModalVisible(false)
          setProcurementLogs([])
          setLogReferences([])
          setLogPage(0)
          setLogHasMore(false)
          setLogLoading(false)
          setLogLoadingMore(false)
        }}
        title={logModalTitle}
        logs={procurementLogs}
        loading={logLoading}
        hasMore={logHasMore}
        loadingMore={logLoadingMore}
        onLoadMore={() => {
          void handleLoadMoreLogs()
        }}
      />
    </div>
  )
}

export default DeliveryTracking
